import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import './FlipBook.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Page aspect for the flip book shell (same ratio as 400×520). */
const PAGE_H_OVER_W = 520 / 400;

const EMBED_W = 400;
const EMBED_H = 520;

function getFullscreenElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function requestFullscreen(el) {
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  return Promise.reject(new Error('Fullscreen not supported'));
}

function exitFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  return Promise.reject(new Error('Exit fullscreen not supported'));
}

/** Two page faces side by side, as large as the viewport allows. */
function computeFullscreenPageSize() {
  const marginX = 32;
  const marginY = 20;
  const toolbar = 52;
  const maxW = window.innerWidth - marginX * 2;
  const maxH = window.innerHeight - marginY * 2 - toolbar;
  if (maxW < 200 || maxH < 200) {
    return { pageW: 200, bookH: Math.round(200 * PAGE_H_OVER_W) };
  }
  const gutter = 16;
  let pageW = Math.floor((maxW - gutter) / 2);
  let bookH = Math.round(pageW * PAGE_H_OVER_W);
  if (bookH > maxH) {
    bookH = maxH;
    pageW = Math.max(180, Math.floor(bookH / PAGE_H_OVER_W));
  }
  pageW = Math.max(180, Math.min(pageW, 800));
  bookH = Math.round(pageW * PAGE_H_OVER_W);
  if (bookH > maxH) {
    bookH = maxH;
    pageW = Math.max(180, Math.floor(bookH / PAGE_H_OVER_W));
  }
  return { pageW, bookH };
}

const PdfPage = React.forwardRef(({ pageNumber, pageWidth }, ref) => {
  return (
    <div className="page" ref={ref}>
      <Page
        pageNumber={pageNumber}
        width={pageWidth}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    </div>
  );
});

function FlipBook({ pdfFile }) {
  const [numPages, setNumPages] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fsLayout, setFsLayout] = useState({ pageW: EMBED_W, bookH: EMBED_H });
  const rootRef = useRef(null);

  const syncFullscreenState = useCallback(() => {
    const el = rootRef.current;
    const fsEl = getFullscreenElement();
    setIsFullscreen(!!el && fsEl === el);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
    };
  }, [syncFullscreenState]);

  useLayoutEffect(() => {
    if (!isFullscreen) {
      setFsLayout({ pageW: EMBED_W, bookH: EMBED_H });
      return;
    }
    const run = () => {
      if (getFullscreenElement() === rootRef.current) {
        setFsLayout(computeFullscreenPageSize());
      }
    };
    run();
    const t = window.setTimeout(run, 0);
    const raf = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => {
      window.clearTimeout(t);
      cancelAnimationFrame(raf);
    };
  }, [isFullscreen]);

  const viewSize = useMemo(
    () => (isFullscreen ? fsLayout : { pageW: EMBED_W, bookH: EMBED_H }),
    [isFullscreen, fsLayout]
  );

  useEffect(() => {
    if (!isFullscreen) return;
    let t;
    const onResize = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        if (getFullscreenElement() === rootRef.current) {
          setFsLayout(computeFullscreenPageSize());
        }
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', onResize);
    };
  }, [isFullscreen]);

  function onDocumentLoadSuccess({ numPages: n }) {
    setNumPages(n);
  }

  const fileToLoad = pdfFile || '/images/about/Handout.pdf';

  const handleFullscreenClick = () => {
    const el = rootRef.current;
    if (!el) return;
    const fsEl = getFullscreenElement();
    if (fsEl === el) {
      exitFullscreen().catch(() => {});
    } else {
      requestFullscreen(el)
        .then(() => {
          setFsLayout(computeFullscreenPageSize());
        })
        .catch(() => {});
    }
  };

  const bookKey = `pdf-flip-${viewSize.pageW}x${viewSize.bookH}`;

  return (
    <div className="flipbook-root" ref={rootRef}>
      <div className="flipbook-toolbar">
        <button
          type="button"
          className="button button--sm flipbook-fullscreen-btn"
          onClick={handleFullscreenClick}
          aria-pressed={isFullscreen}
        >
          {isFullscreen ? 'Exit full screen' : 'View full screen'}
        </button>
      </div>
      <div className="flipbook-container">
        <Document
          file={fileToLoad}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div className="flipbook-message">Loading flipbook…</div>}
          error={<div className="flipbook-message">Failed to load PDF.</div>}
        >
          {numPages && (
            <HTMLFlipBook
              key={bookKey}
              width={viewSize.pageW}
              height={viewSize.bookH}
              size="fixed"
              minWidth={isFullscreen ? 150 : 200}
              maxWidth={isFullscreen ? 2000 : 600}
              minHeight={isFullscreen ? 200 : 260}
              maxHeight={isFullscreen ? 2000 : 780}
              maxShadowOpacity={0.5}
              showCover
              mobileScrollSupport
              className="pdf-flipbook"
            >
              {Array.from(new Array(numPages), (_, index) => (
                <PdfPage
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  pageWidth={viewSize.pageW}
                />
              ))}
            </HTMLFlipBook>
          )}
        </Document>
      </div>
    </div>
  );
}

export default FlipBook;
